// Screen navigation constants
export const SCREENS = {
  WELCOME: 'WELCOME',
  ORIENTATION_SELECTION: 'ORIENTATION_SELECTION',
  CAMERA: 'CAMERA',
  STYLE_SELECTION: 'STYLE_SELECTION',
  LOADING: 'LOADING',
  RESULT: 'RESULT',
  QR_CODE: 'QR_CODE',
  IMAGE_VIEWER: 'IMAGE_VIEWER'
};

// Universal quality enhancers - Aggressively tuned for REALISM over "art"
// UPDATED: Added "film grain, raw photo, fujifilm" to force texture and remove the plastic look
export const QUALITY_KEYWORDS = 'raw photo, unedited, 8k, hyperrealistic, photorealistic, visible skin pores, skin texture, imperfections, soft film grain, depth of field, raytracing, global illumination, shot on Sony A7R IV, 85mm lens, f/1.8, cinematic lighting, volumetric fog';

// Universal negative prompt
// UPDATED: Strictly forbids the "smooth" or "3D" look
export const UNIVERSAL_NEGATIVE_PROMPT = 'cgi, 3d render, painting, illustration, drawing, cartoon, anime, doll, plastic, wax figure, photoshop, airbrushed, smooth skin, blurred details, low quality, blurry, jpeg artifacts, floating limbs, distorted face, bad anatomy, makeup, fake, glossy skin';

// Environment-specific negative additions
export const ENVIRONMENT_NEGATIVE = 'floating head, floating body, cut off at bottom, sticker effect, green screen look, tiny subject, distant subject, giant background, mismatched perspective, bad composite, hard edges';

// Transformation-specific negative additions
// CRITICAL: No gender mixing - masculine features for men, feminine features for women
// CRITICAL: NO LOGOS OR BRANDING - This is an official event, any wrong logo is a legal risk
export const TRANSFORMATION_NEGATIVE = 'any logo, any emblem, any badge, any branding, any insignia, any crest, any seal, any symbol on clothing, any organizational logo, any company logo, any government logo, any police logo, any official emblem, text on clothing, printed graphics on clothing, gender mixing, feminine features on male, masculine features on female, earrings on male, jewelry on male, lipstick on male, feminine accessories on male, masculine jaw on female, facial hair on female, wrong flag colors, incorrect flag design, helmet, headgear, hat, cap, face mask, weird neck, different skin tone, extra fingers, deformed hands, holding object weirdly, floating object, cartoon eyes, glowing eyes, text watermark';

// CRITICAL ACCURACY REQUIREMENTS - Add to transformation prompts
export const ACCURACY_REQUIREMENTS = `
*** CRITICAL ACCURACY REQUIREMENTS ***
1. GENDER CONSISTENCY: If the person is male, ALL features must be masculine (no earrings, no feminine accessories, no feminine facial features). If female, ALL features must be feminine. NO gender mixing whatsoever.
2. NO LOGOS OR BRANDING: Do NOT generate ANY logos, emblems, badges, crests, seals, insignias, or organizational symbols. This includes police logos, government emblems, company logos, or any official branding. Clothing must be PLAIN without any printed logos or text. This is a legal requirement.
3. NO FLAGS: Do NOT include any national flags or official symbols in the image.
4. PLAIN CLOTHING: All clothing must be solid colors without any printed graphics, logos, text, or branding of any kind.
`;

// Dubai Police logo path (used as reference image for transformation styles)
export const DUBAI_POLICE_LOGO = '/dubai-police-logo-2x3.png';

// Cloudflare URL for Dubai Police logo (for faster webhook calls)
export const DUBAI_POLICE_LOGO_URL = 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T12:00:47.178Z-1763812851049-q1a13fl.jpg';

// Cloudflare R2 URLs for style reference images (compressed, faster loading)
export const CLOUDFLARE_STYLE_URLS = {
  // Environment styles
  support_center: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914030083-rq015ac.jpg',
  community_garden: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914035483-qkxw18k.jpg',
  awareness_stage: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914040053-wyf9x6j.jpg',
  dubai_skyline: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914055523-dhzxt8k.jpg',
  safe_haven: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914062399-y19hfx3.jpg',
  education_center: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914076720-6uvcq41.jpg',
  // Transformation styles
  healthcare_professional: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-24T01:00:00.000Z-1763995060240-h9vbw4f.jpg',
  police_officer: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914083554-yf11zss.jpg',
  advocate: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914094443-4rir3gh.jpg',
  counselor: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914100895-tbzwx6z.jpg',
  educator: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914107606-06ab7zb.jpg',
  volunteer: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914114213-lzkzoxv.jpg',
  leader: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-23T01:00:00.000Z-1763914120919-siqaemx.jpg',
};

// ----------------------------------------------------------------------
// MODE 1: ENVIRONMENT STYLES (Background Changes Only)
// Text-based descriptions - AI generates background from description
// Prompts are comprehensive descriptions for women protection event
// ----------------------------------------------------------------------
export const ENVIRONMENT_STYLES = {
  SUPPORT_CENTER: {
    id: 'support_center',
    name: 'Support Center',
    nameAr: 'مركز الدعم',
    description: 'Modern counseling room with warm, welcoming atmosphere.',
    tags: ['Scene Swap', 'Support'],
    referenceImage: CLOUDFLARE_STYLE_URLS.support_center,
    previewImage: '/Style Images/Environment/support-center.png',
    gradient: 'from-green-600 to-teal-600',
    generationMode: 'environment',
    prompt: `Bright, welcoming modern support center interior. Warm natural lighting from large windows with sheer white curtains. Comfortable seating area with plush armchairs in calming sage green and cream colors arranged in a conversational layout. A small coffee table with fresh flowers and tissue box. Soft beige walls with subtle texture. On one wall, a large canvas with an inspirational message about strength and support. Dubai Police green accent colors in decorative elements - cushions, a plant pot, artwork frame. Potted indoor plants adding life to the space - a fiddle leaf fig in the corner, small succulents on shelves. Warm wooden flooring. A desk area in the background with a computer, suggesting professional services available. Calming, safe, professional yet comfortable atmosphere. Natural daylight creating a hopeful, positive ambiance.`
  },
  COMMUNITY_GARDEN: {
    id: 'community_garden',
    name: 'Community Garden',
    nameAr: 'حديقة المجتمع',
    description: 'Peaceful outdoor Dubai park with greenery and benches.',
    tags: ['Scene Swap', 'Peaceful'],
    referenceImage: CLOUDFLARE_STYLE_URLS.community_garden,
    previewImage: '/Style Images/Environment/community-garden.png',
    gradient: 'from-emerald-500 to-green-600',
    generationMode: 'environment',
    prompt: `Beautiful daytime Dubai community park scene. Lush green manicured lawn stretching into the distance. Mature palm trees providing dappled shade. Colorful flower beds with pink bougainvillea, white jasmine, and orange marigolds. A curved walking path made of light beige stone winding through the garden. Elegant wooden benches with green metal frames placed along the path. In the background, the modern Dubai skyline visible through the trees - glass towers catching the sunlight. Clear blue sky with a few wispy white clouds. Golden hour lighting creating warm, inviting atmosphere. A decorative fountain or water feature visible in the mid-ground. Well-maintained hedges and ornamental grasses. Peaceful, serene, family-friendly public space atmosphere.`
  },
  AWARENESS_STAGE: {
    id: 'awareness_stage',
    name: 'Awareness Stage',
    nameAr: 'منصة التوعية',
    description: 'Event stage with professional lighting for speaking.',
    tags: ['Scene Swap', 'Event'],
    referenceImage: CLOUDFLARE_STYLE_URLS.awareness_stage,
    previewImage: '/Style Images/Environment/awareness-stage.png',
    gradient: 'from-purple-600 to-orange-500',
    generationMode: 'environment',
    prompt: `Professional conference stage setup for awareness event. Elevated stage platform with dark polished floor. Large LED screen backdrop displaying Dubai Police logo (green emblem) and text "Women Protection Platform" / "منصة حماية المرأة" in green and white. Professional stage lighting - warm spotlights from above, subtle purple and orange accent lights on the sides representing awareness colors. A sleek modern podium/lectern in white with Dubai Police branding. Two comfortable armchairs on stage for panel discussions. Professional microphone stands. The audience area visible in soft focus foreground - rows of seated attendees. Banners with supportive messages visible on side panels. Corporate event atmosphere with warm, empowering energy. Professional photography lighting setup.`
  },
  DUBAI_SKYLINE: {
    id: 'dubai_skyline',
    name: 'Dubai Skyline',
    nameAr: 'أفق دبي',
    description: 'Inspiring Dubai cityscape at golden hour.',
    tags: ['Scene Swap', 'Iconic'],
    referenceImage: CLOUDFLARE_STYLE_URLS.dubai_skyline,
    previewImage: '/Style Images/Environment/dubai-skyline.png',
    gradient: 'from-orange-500 to-pink-500',
    generationMode: 'environment',
    prompt: `Stunning Dubai skyline at golden hour/sunset. The iconic Burj Khalifa tower prominently centered, its silver facade glowing warm orange and pink from the setting sun. Surrounding modern skyscrapers of varying heights creating a dramatic cityscape. The sky filled with beautiful gradient colors - deep orange near the horizon transitioning to soft pink, then light purple higher up. A few golden clouds catching the last rays of sunlight. In the foreground, a modern observation deck or rooftop terrace with glass railings and polished floor reflecting the sky colors. The terrace has minimalist outdoor furniture - a bench or seating area. City lights beginning to twinkle in the buildings below. Inspirational, aspirational atmosphere representing progress and hope. Magazine-quality cityscape photography.`
  },
  SAFE_HAVEN: {
    id: 'safe_haven',
    name: 'Safe Haven',
    nameAr: 'ملاذ آمن',
    description: 'Cozy, secure indoor reading nook with warm lighting.',
    tags: ['Scene Swap', 'Comfort'],
    referenceImage: CLOUDFLARE_STYLE_URLS.safe_haven,
    previewImage: '/Style Images/Environment/safe-haven.png',
    gradient: 'from-amber-500 to-orange-400',
    generationMode: 'environment',
    prompt: `Cozy, intimate indoor reading nook or quiet corner. Warm ambient lighting from a stylish floor lamp with fabric shade casting a soft golden glow. A comfortable oversized armchair in warm cream or soft terracotta fabric with plush cushions. A soft knitted throw blanket draped over the chair arm. Small wooden side table with a cup of tea, a small plant, and a few books stacked. Built-in bookshelf in the background filled with colorful book spines. Soft area rug in warm neutral tones on wooden flooring. Sheer curtains filtering soft natural light from a window. A few framed inspirational quotes or artwork on the warm-toned walls. Candle or diffuser adding to the peaceful atmosphere. Safe, nurturing, private sanctuary feeling. Hygge-inspired comfortable interior design.`
  },
  EDUCATION_CENTER: {
    id: 'education_center',
    name: 'Education Center',
    nameAr: 'مركز التعليم',
    description: 'Modern workshop space for learning and empowerment.',
    tags: ['Scene Swap', 'Learning'],
    referenceImage: CLOUDFLARE_STYLE_URLS.education_center,
    previewImage: '/Style Images/Environment/education-center.png',
    gradient: 'from-blue-600 to-green-600',
    generationMode: 'environment',
    prompt: `Modern, bright educational workshop space. Clean white walls with large windows letting in abundant natural light. Contemporary classroom setup with curved modular tables arranged in a collaborative U-shape or small group clusters. Ergonomic chairs in Dubai Police green color. A large interactive digital whiteboard/smart screen on the front wall displaying educational content - graphs, empowering messages. Motivational posters and infographics on the walls about rights, support resources, and empowerment. A presenter's area with a sleek white desk and laptop. Potted plants adding freshness to the space. Modern pendant lighting fixtures. A small refreshment station visible in the corner with water and coffee. Professional flip charts and workshop materials on tables. Bright, energizing, professional learning environment atmosphere.`
  }
};

// ----------------------------------------------------------------------
// MODE 2: TRANSFORMATION STYLES (Outfit + Background Changes)
// Prompts are comprehensive descriptions for women protection event
// ----------------------------------------------------------------------
export const TRANSFORMATION_STYLES = {
  HEALTHCARE_PROFESSIONAL: {
    id: 'healthcare_professional',
    name: 'Healthcare Professional',
    nameAr: 'أخصائية رعاية صحية',
    description: 'Medical professional providing care and support.',
    tags: ['Role', 'Healthcare'],
    referenceImage: CLOUDFLARE_STYLE_URLS.healthcare_professional,
    previewImage: '/Style Images/Full Transformation/healthcare-professional.png',
    gradient: 'from-teal-600 to-cyan-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Healthcare Professional in a modern medical setting.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES, NO TEXT):
- Clean white medical lab coat (plain white, NO logos, NO name tags, NO hospital branding)
- White or light blue hijab if female
- Light blue or teal medical scrubs visible underneath (plain solid color, no prints)
- Professional stethoscope around neck
- All clothing must be completely plain solid colors - NO logos, NO emblems, NO text, NO badges, NO hospital names

POSE & ACTION:
- Standing confidently in medical environment
- Warm, reassuring smile
- Stethoscope around neck or in hand
- Hands in professional, caring gesture
- Compassionate yet professional expression
- Body language conveying trust and expertise

ENVIRONMENT (NO LOGOS OR TEXT VISIBLE ANYWHERE):
- Modern, bright medical clinic or hospital setting
- Clean white/light gray walls
- Medical equipment softly blurred in background (NO visible brand names or logos)
- Natural light from windows
- Professional healthcare environment
- CRITICAL: No hospital names, no medical brand logos, no text visible anywhere

PHOTOGRAPHY STYLE: Professional healthcare photography, PHOTOREALISTIC, real camera photograph look, bright clean lighting, clinical yet warm tones, f/2.0 shallow depth of field, sharp focus on subject, natural skin pores and texture visible, trustworthy medical professional feel, NOT painted or illustrated, 2:3 vertical format.`
  },
  ADVOCATE: {
    id: 'advocate',
    name: 'Community Advocate',
    nameAr: 'مناصرة المجتمع',
    description: 'Professional speaker empowering others at an event.',
    tags: ['Role', 'Speaker'],
    referenceImage: CLOUDFLARE_STYLE_URLS.advocate,
    previewImage: '/Style Images/Full Transformation/community-advocate.png',
    gradient: 'from-purple-600 to-pink-500',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into a Community Advocate speaking at an empowerment event.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES):
- Elegant black abaya (flowing black robe) worn open over clothing
- Emerald green dress or top visible underneath the abaya
- Black hijab if female
- Small headset microphone near cheek for speaking
- Subtle makeup enhancing natural features
- All clothing must be completely plain without any logos, emblems, or branding

POSE & ACTION:
- Dynamic speaking pose, captured mid-gesture
- One hand raised expressively while making a point
- Other hand in natural speaking gesture
- Passionate, engaged facial expression
- Eyes bright with conviction
- Body angled slightly, natural speaking posture
- Radiating confidence and inspiration

ENVIRONMENT (NO LOGOS OR TEXT ON SCREENS):
- Professional conference stage setting
- Warm spotlight illuminating the speaker
- Large screen behind showing plain solid color or abstract gradient (NO logos, NO text, NO branding on screen)
- Soft purple and orange accent lighting on stage edges
- Blurred audience visible in foreground
- Professional event atmosphere with premium production quality

PHOTOGRAPHY STYLE: Event photography, PHOTOREALISTIC, real camera photograph look, dramatic stage lighting, warm tones, f/2.0 shallow depth of field, sharp focus on speaker, natural skin pores and texture visible, editorial quality, NOT painted or illustrated, 2:3 vertical format.`
  },
  COUNSELOR: {
    id: 'counselor',
    name: 'Support Counselor',
    nameAr: 'مستشارة دعم',
    description: 'Warm, approachable professional providing guidance.',
    tags: ['Role', 'Support'],
    referenceImage: CLOUDFLARE_STYLE_URLS.counselor,
    previewImage: '/Style Images/Full Transformation/support-counselor.png',
    gradient: 'from-teal-500 to-cyan-500',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Support Counselor in a welcoming office setting.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES):
- Professional sage green blazer/suit jacket (solid color, no logos)
- Cream or beige hijab if female
- Simple cream blouse underneath (plain, no prints)
- Lanyard around neck with plain ID card (NO logos on lanyard or card, just solid colors)
- Minimal, tasteful jewelry
- Professional but approachable appearance
- All clothing must be completely plain without any logos, emblems, or branding

POSE & ACTION:
- Seated in comfortable beige armchair
- Holding open notepad and pen, as if taking notes
- Attentive, focused expression showing active listening
- Body language conveying empathy and understanding
- Eyes showing kindness and professionalism
- Approachable, trustworthy demeanor

ENVIRONMENT (NO LOGOS OR TEXT VISIBLE):
- Cozy, private counseling office with warm beige/cream walls
- Comfortable armchair where counselor is seated
- Soft warm lighting from table lamp visible
- Small side table with tissues box and glass of water
- Plain framed certificate on wall behind (NO logos, NO readable text, just decorative frame)
- Warm, safe, confidential atmosphere

PHOTOGRAPHY STYLE: Lifestyle portrait photography, PHOTOREALISTIC, real camera photograph look, warm natural lighting, soft tones, f/2.0 depth of field, sharp focus on counselor, natural skin pores and texture visible, authentic and welcoming feel, NOT painted or illustrated, 2:3 vertical format.`
  },
  EDUCATOR: {
    id: 'educator',
    name: 'Educator',
    nameAr: 'معلمة',
    description: 'Teacher facilitating a workshop on awareness.',
    tags: ['Role', 'Teaching'],
    referenceImage: CLOUDFLARE_STYLE_URLS.educator,
    previewImage: '/Style Images/Full Transformation/educator.png',
    gradient: 'from-blue-600 to-indigo-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into an Educator leading an awareness workshop.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES):
- Elegant black abaya (flowing black robe)
- Black hijab if female
- White blouse or top visible at neckline
- Holding a marker or pointer in hand
- All clothing must be completely plain without any logos, emblems, or branding

POSE & ACTION:
- Standing at front of modern training room
- One hand gesturing toward the interactive screen
- Other hand holding a marker
- Engaged, enthusiastic expression while explaining
- Making eye contact as if teaching to audience
- Dynamic teaching posture, mid-explanation

ENVIRONMENT (NO LOGOS OR BRANDING ON ANY SCREENS):
- Modern bright corporate training room
- Large interactive touchscreen/smart display showing bar charts and graphs with title "EMPOWERMENT STRATEGIES" (NO logos anywhere on screen)
- Flip chart stand beside the screen with handwritten notes
- Floor-to-ceiling windows showing Dubai cityscape in background
- Modern gray carpeted floor
- Seating area visible in soft focus
- Professional corporate training environment

PHOTOGRAPHY STYLE: Documentary education photography, PHOTOREALISTIC, real camera photograph look, bright even lighting, f/2.8 depth of field, sharp focus on educator, natural skin pores and texture visible, candid teaching moment, NOT painted or illustrated, 2:3 vertical format.`
  },
  VOLUNTEER: {
    id: 'volunteer',
    name: 'Volunteer',
    nameAr: 'متطوعة',
    description: 'Community volunteer helping at an awareness event.',
    tags: ['Role', 'Community'],
    referenceImage: CLOUDFLARE_STYLE_URLS.volunteer,
    previewImage: '/Style Images/Full Transformation/volunteer.png',
    gradient: 'from-orange-500 to-amber-500',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Volunteer at a community awareness event.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES, NO TEXT):
- Plain white polo shirt (solid color only, absolutely no logos, no prints, no text)
- White hijab if female
- Bright orange/coral high-visibility safety vest (plain solid color, NO logos, NO text, NO prints on vest)
- Plain lanyard around neck with simple ID card (NO logos on lanyard or card)
- Holding brochures or pamphlets in one hand
- CRITICAL: All clothing must be completely plain solid colors - NO logos, NO emblems, NO text, NO badges, NO branding of any kind

POSE & ACTION:
- Standing facing camera with welcoming posture
- Bright, genuine smile
- One hand giving thumbs up gesture
- Other hand holding informational brochures
- Approachable, friendly, ready-to-help body language
- Radiating positive community spirit

ENVIRONMENT (ALL ELEMENTS MUST BE PLAIN - NO LOGOS OR TEXT ANYWHERE):
- Outdoor waterfront community event venue
- White event tents/canopies visible in background (plain white, NO logos, NO text)
- Crowd of people mingling in soft focus background
- Purple/maroon plain banners or flags visible (solid colors only, NO logos, NO text)
- Water/sea visible in far background
- Bright daylight, clear sky
- Festive community event atmosphere
- CRITICAL: All background elements must be plain solid colors - absolutely NO logos, NO text, NO emblems, NO branding visible anywhere in the scene

PHOTOGRAPHY STYLE: Event photography, PHOTOREALISTIC, real camera photograph look, bright natural daylight, warm tones, f/2.8 depth of field, sharp focus on volunteer, natural skin pores and texture visible, authentic community event feel, NOT painted or illustrated, 2:3 vertical format.`
  },
  LEADER: {
    id: 'leader',
    name: 'Community Leader',
    nameAr: 'قائدة مجتمعية',
    description: 'Empowered professional businesswoman inspiring change.',
    tags: ['Role', 'Leadership'],
    referenceImage: CLOUDFLARE_STYLE_URLS.leader,
    previewImage: '/Style Images/Full Transformation/community-leader.png',
    gradient: 'from-amber-600 to-orange-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into an empowered Community Leader and businesswoman.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, and natural appearance. This is the most critical requirement.

OUTFIT (ALL CLOTHING MUST BE PLAIN - NO LOGOS, NO EMBLEMS, NO BADGES):
- Elegant black abaya with subtle gold embroidery/trim details
- Black hijab if female
- Gold decorative border along abaya edges
- Sophisticated, regal appearance
- All clothing must be completely plain without any logos, emblems, or branding

POSE & ACTION:
- Standing confidently near executive desk by window
- One hand resting on desk surface
- Other hand near a crystal/glass award trophy (plain crystal, NO logos or text engraved)
- Dignified, composed expression
- Direct confident gaze at camera
- Body language exuding leadership and accomplishment
- Posture of someone who has achieved and empowers others

ENVIRONMENT (NO LOGOS OR TEXT VISIBLE):
- Luxurious Dubai executive office with floor-to-ceiling windows
- Stunning Dubai skyline visible through windows - Burj Khalifa prominently visible in background
- Elegant wooden executive desk
- Plain crystal/glass award trophy on desk (NO logos, NO text, just decorative crystal shape)
- Premium office with warm wood paneling
- Soft natural daylight from windows
- Aspirational, successful professional environment

PHOTOGRAPHY STYLE: Executive portrait photography, PHOTOREALISTIC, real camera photograph look, dramatic natural window light, warm golden tones, f/2.0 shallow depth of field, sharp focus on subject, natural skin pores and texture visible, magazine cover quality, NOT painted or illustrated, 2:3 vertical format.`
  }
};

// Legacy STYLES object for backwards compatibility - defaults to environment styles
export const STYLES = ENVIRONMENT_STYLES;

// Timing constants
export const COUNTDOWN_DURATION = 3; // seconds
export const AUTO_RESET_TIMEOUT = 60000; // 60 seconds

// Loading messages for generating images
export const GENERATING_MESSAGES = [
  'Analyzing image composition...',
  'Processing transformation...',
  'Creating your empowering look...',
  'Enhancing image quality...',
  'Finalizing your photo...'
];

// Loading messages for saving images
export const SAVING_MESSAGES = [
  'Preparing your image...',
  'Uploading to cloud storage...',
  'Generating download link...',
  'Creating QR code...',
  'Almost ready!'
];

// Legacy export for backwards compatibility
export const LOADING_MESSAGES = GENERATING_MESSAGES;

// Firebase collection path
export const IMAGES_COLLECTION = 'generated_images';

// TTL duration (24 hours in milliseconds)
export const IMAGE_TTL = 24 * 60 * 60 * 1000;